import { useState } from "react";
import { httpsCallable } from "firebase/functions";
import {
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
} from "firebase/auth";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { auth, functions } from "../firebase";

const LEGAL_VERSION = "1.0-2026-11-01";
const LEGAL_BASE_URL = "https://www.vexadent.com";
const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;

export default function LoginScreenV2() {
  const registrationQuery = new URLSearchParams(window.location.search);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [nombre, setNombre] = useState("");
  const [isRegister, setIsRegister] = useState(registrationQuery.get("registro") === "1");
  const [billingInterval, setBillingInterval] = useState(registrationQuery.get("interval") === "year" ? "year" : "month");
  const [loading, setLoading] = useState(false);
  const [legalConsent, setLegalConsent] = useState(false);

  const startCheckout = async () => {
    const result = await httpsCallable(functions, "createStripeCheckoutSession")({ plan: "standard", interval: billingInterval });
    if (result.data?.url) window.location.assign(result.data.url);
  };

  const resetPassword = async () => {
    if (!email) {
      alert("Escribe tu correo electrónico para enviarte el enlace.");
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      alert("Te enviamos un correo para restablecer tu contraseña.");
    } catch (err) {
      console.error(err);
      alert(err.code === "auth/user-not-found" ? "No existe una cuenta con ese correo." : "No fue posible enviar el correo.");
    }
  };

  const loginEmail = async () => {
    if (!email || !password) {
      alert("Completa tu correo y contraseña.");
      return;
    }
    try {
      setLoading(true);
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      console.error(err);
      alert(err.code === "auth/invalid-credential" ? "Correo o contraseña incorrectos." : err.message);
    } finally {
      setLoading(false);
    }
  };

  const register = async () => {
    if (!nombre || !email || !password) {
      alert("Completa todos los campos.");
      return;
    }
    if (!legalConsent) {
      alert("Debes aceptar los términos y el aviso de privacidad para registrarte.");
      return;
    }
    if (!passwordRegex.test(password)) {
      alert("La contraseña debe contener:\n\n- 8 caracteres\n- Una mayúscula\n- Una minúscula\n- Un número\n- Un carácter especial");
      return;
    }
    try {
      setLoading(true);
      await createUserWithEmailAndPassword(auth, email, password);
      await httpsCallable(functions, "ensureClinicAccount")({ nombre, legalConsent: { version: LEGAL_VERSION } });
      await startCheckout();
    } catch (err) {
      console.error(err);
      alert(err.code === "auth/email-already-in-use" ? "Ese correo ya está registrado." : "No pudimos crear la cuenta. Intenta nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  const submitForm = (event) => {
    event.preventDefault();
    if (isRegister) register();
    else loginEmail();
  };

  return (
    <div className="vd-login-screen">
      <div className="vd-login-orb vd-login-orb-one" />
      <div className="vd-login-orb vd-login-orb-two" />
      <main className="vd-login-shell">
        <aside className="vd-login-showcase" aria-label="VexaDent">
          <div className="vd-login-brand"><img src="/vexadent-mark.png" alt="" /><span>Vexa<span>Dent</span></span></div>
          <div className="vd-login-showcase-copy"><p className="vd-login-kicker"><span /> Plataforma para consultorios dentales</p><h1>Tu clínica, <em>más clara.</em></h1><p>Agenda, expediente, tomografías, tratamientos, pagos y seguimiento en un mismo lugar.</p></div>
          <div className="vd-login-preview"><div className="vd-login-preview-top"><span><i /><i /><i /></span><small>VexaDent / Hoy</small><b>EV</b></div><div className="vd-login-preview-content"><div className="vd-login-preview-title"><span><small>PRÓXIMA CITA</small><strong>Mariana Ríos</strong></span><b>10:30</b></div><div className="vd-login-preview-grid"><span><b>✓</b><small>Cita confirmada</small></span><span><b>◷</b><small>WhatsApp automático</small></span><span><b>$</b><small>Saldo visible</small></span></div></div></div>
          <div className="vd-login-showcase-footer"><span><b>01</b> Menos pestañas abiertas</span><span><b>02</b> Más control para atender</span></div>
        </aside>

        <section className="vd-login-form-panel">
          <div className="vd-login-form-top"><span className="vd-login-secure"><span /> Acceso seguro</span><span className="vd-login-help">¿Necesitas ayuda? <a href="mailto:admin@vexadent.com">admin@vexadent.com</a></span></div>
          <div className="vd-login-form-content">
            <div className="vd-login-form-heading"><p className="vd-login-eyebrow">{isRegister ? "Empieza con VexaDent" : "Bienvenido de vuelta"}</p><h2>{isRegister ? "Crea el control de tu consultorio." : "Entra a tu consultorio."}</h2><p>{isRegister ? "Crea tu cuenta, elige tu plan y continúa al pago seguro." : "Accede a tu agenda, expedientes y operación clínica."}</p></div>
            {isRegister && <div className="vd-login-plan-picker" aria-label="Selecciona tu plan"><button type="button" className={`vd-login-plan-option ${billingInterval === "month" ? "is-selected" : ""}`} onClick={() => setBillingInterval("month")}><span><b>Mensual</b><small>Flexibilidad total</small></span><strong>$799 <small>MXN / mes</small></strong></button><button type="button" className={`vd-login-plan-option ${billingInterval === "year" ? "is-selected" : ""}`} onClick={() => setBillingInterval("year")}><span><b>Anual</b><small>Dos meses sin costo</small></span><strong>$7,990 <small>MXN / año</small></strong></button></div>}
            <form className="vd-login-form" onSubmit={submitForm}>
              {isRegister && <label className="vd-login-field"><span>Nombre del consultorio</span><input type="text" placeholder="Ej. Clínica Valle" value={nombre} onChange={e => setNombre(e.target.value)} autoComplete="organization" /></label>}
              <label className="vd-login-field"><span>Correo electrónico</span><input type="email" placeholder="tu@consultorio.com" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" /></label>
              <label className="vd-login-field"><span>Contraseña</span><span className="vd-login-password-wrap"><input type={showPassword ? "text" : "password"} placeholder="Escribe tu contraseña" value={password} onChange={e => setPassword(e.target.value)} autoComplete={isRegister ? "new-password" : "current-password"} /><button type="button" className="vd-login-password-toggle" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}>{showPassword ? <FaEyeSlash size={17} /> : <FaEye size={17} />}</button></span></label>
              {isRegister && <p className="vd-login-password-hint">Mínimo 8 caracteres, con mayúscula, minúscula, número y símbolo.</p>}
              {isRegister && <label className="vd-login-consent"><input type="checkbox" checked={legalConsent} onChange={e => setLegalConsent(e.target.checked)} /><span>Acepto los <a href={`${LEGAL_BASE_URL}/terminos`} target="_blank" rel="noreferrer">Términos y condiciones</a> y el <a href={`${LEGAL_BASE_URL}/privacidad`} target="_blank" rel="noreferrer">Aviso de privacidad</a>. Confirmo que leí la información del cobro y la cancelación.</span></label>}
              <button className="vd-login-submit" type="submit" disabled={loading}>{loading ? "Procesando..." : isRegister ? "Crear cuenta y continuar" : "Entrar a VexaDent"}<span>→</span></button>
            </form>
            {!isRegister && <button className="vd-login-forgot" type="button" onClick={resetPassword}>¿Olvidaste tu contraseña?</button>}
            <div className="vd-login-switch"><span>{isRegister ? "¿Ya tienes cuenta?" : "¿Aún no tienes acceso?"}</span><button type="button" onClick={() => setIsRegister(!isRegister)}>{isRegister ? "Iniciar sesión" : "Crear cuenta"}</button></div>
            <p className="vd-login-note">{isRegister ? "Al crear tu cuenta podrás continuar al checkout seguro de Stripe." : "Usa el correo y contraseña con los que registraste tu consultorio."}</p>
          </div>
        </section>
      </main>
    </div>
  );
}
